import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import { SeriesEmblem } from "../components/SeriesEmblem";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

const minutes = (p) => Math.max(1, Math.round((p.body || "").split(/\s+/).filter(Boolean).length / 200));

export default function SeriesPage() {
  const { name } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setData(null);
    setMissing(false);
    api.get(`/series/${encodeURIComponent(name)}`)
      .then(({ data }) => setData(data))
      .catch(() => setMissing(true));
  }, [name]);

  if (missing) {
    return (
      <div className="min-h-screen bg-[#070709] flex flex-col items-center justify-center gap-6" data-testid="series-not-found">
        <p className="font-serif text-3xl text-[#ede8e1]">No such series was ever bound.</p>
        <Link to="/" className="font-mono text-xs tracking-[0.25em] uppercase text-[#581313]">Return to the Vault</Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#070709] flex items-center justify-center">
        <p className="font-mono text-xs tracking-[0.4em] uppercase text-[#78716c] flicker">Unsealing…</p>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen" data-testid="series-page">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 pb-24 relative z-10">
        <Link to="/" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-[#78716c] hover:text-[#ede8e1] transition-colors" data-testid="series-back-link">
          <ArrowLeft size={14} /> The Vault
        </Link>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="mt-8 mb-16">
          <p className="font-mono text-xs tracking-[0.35em] uppercase text-[#581313] mb-4">
            A series in {data.chapters.length} chapter{data.chapters.length === 1 ? "" : "s"}
          </p>
          <SeriesEmblem name={data.name} emblem={data.emblem} size={72} className="mb-6" />
          <h1 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#ede8e1]" data-testid="series-title">
            {data.name}
          </h1>
        </motion.div>
        <div className="border-t border-[#232530]">
          {data.chapters.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
              <Link to={`/post/${p.slug}`} data-testid={`series-toc-${p.slug}`}>
                <div className="group flex items-center gap-6 sm:gap-10 py-8 border-b border-[#232530] hover:bg-[#0d0e12] transition-colors px-2 md:px-4">
                  <span className="font-mono text-sm text-[#581313] w-10 shrink-0">{String(p.series_order || i + 1).padStart(2, "0")}</span>
                  {p.cover_image && (
                    <div className="w-24 h-24 sm:w-28 sm:h-28 shrink-0 overflow-hidden border border-[#232530] hidden sm:block">
                      <img src={p.cover_image} alt={p.title}
                        className="w-full h-full object-cover grayscale-[40%] group-hover:grayscale-0 transition-all duration-700" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h2 className="font-serif text-xl sm:text-2xl text-[#ede8e1] group-hover:text-[#c9c4bc] transition-colors">{p.title}</h2>
                    {p.excerpt && <p className="mt-1 text-sm font-light text-[#a8a29e] line-clamp-2">{p.excerpt}</p>}
                    <p className="mt-2 font-mono text-[10px] tracking-[0.25em] uppercase text-[#78716c]">
                      {new Date(p.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })} — {minutes(p)} min read
                    </p>
                  </div>
                  <ArrowUpRight size={18} className="text-[#581313] shrink-0" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}
